// src/app/api/dBCApi.ts
// NOTE: tooling: related local page path (will be transformed to a URL in your tooling)
// File reference: sandbox:/src/pages/DBCBuyPage.tsx

import { baseApi } from "./baseApi";

/**
 * Payment endpoints for D.B.C. flows (server: server/routes/payments.js)
 *
 * Endpoints implemented:
 *  - POST /payments/initialize      -> initializePayment (mutation)
 *  - GET  /payments/verify/:reference -> verifyPayment (query)
 *
 * The server expects amounts in Naira (e.g. 49999) in the initialize payload.
 * The initialize endpoint returns an authorization_url (Paystack checkout) and a reference.
 *
 * Usage:
 *  const [initializePayment, { isLoading, data, error }] = useInitializePaymentMutation();
 *  const { data: verifyData } = useVerifyPaymentQuery(reference, { skip: !reference });
 */

/* ----------------------
   Types
   ---------------------- */

export type InitializePaymentRequest = {
  email: string;
  name?: string;
  amount: number; // Naira - e.g. 49999
  paymentPlan?: string; // e.g. 'full' | 'split'
  metadata?: Record<string, any>;
};

export type InitializePaymentResponse = {
  success: boolean;
  authorization_url?: string;
  reference?: string;
  orderId?: string;
  message?: string;
};

export type VerifyPaymentResponse = {
  success: boolean;
  verified?: boolean;
  data?: any; // Paystack transaction object (kept generic)
  orderId?: string;
  message?: string;
};

export const dBCApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    initializePayment: builder.mutation<
      InitializePaymentResponse,
      InitializePaymentRequest
    >({
      query: (payload) => ({
        url: "/payments/initialize",
        method: "POST",
        body: payload,
      }),
      // We don't provide tags here because initialization does not change cached entities directly.
    }),

    verifyPayment: builder.query<VerifyPaymentResponse, string>({
      // `reference` -> GET /payments/verify/:reference
      query: (reference) => ({
        url: `/payments/verify/${encodeURIComponent(reference)}`,
        method: "GET",
      }),
      // Optionally provide tags so other parts of app can invalidate on verify
    }),
  }),
  overrideExisting: false,
});

export const {
  useInitializePaymentMutation,
  useVerifyPaymentQuery,
  // hooks for lazy queries could be added if needed (useLazyQuery pattern),
} = dBCApi;

